import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Play, 
  UserPlus, 
  Sparkles, 
  Target, 
  BookOpen, 
  Trophy,
  ExternalLink,
  Smartphone,
  Monitor
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface TestFlow {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  queryParam?: string;
}

const TEST_FLOWS: TestFlow[] = [
  {
    id: 'onboarding-profile',
    name: 'Onboarding: Profile Setup',
    description: 'Test the profile creation step',
    icon: <UserPlus className="w-4 h-4" />,
    path: '/onboarding/profile',
    queryParam: 'preview=1'
  },
  {
    id: 'onboarding-genres',
    name: 'Onboarding: Genre Selection',
    description: 'Test the genre preferences step',
    icon: <BookOpen className="w-4 h-4" />,
    path: '/onboarding/genres',
    queryParam: 'preview=1'
  },
  {
    id: 'onboarding-archetype',
    name: 'Onboarding: Reader Quiz',
    description: 'Test the archetype quiz flow',
    icon: <Sparkles className="w-4 h-4" />,
    path: '/onboarding/archetype',
    queryParam: 'preview=1'
  },
  {
    id: 'onboarding-results',
    name: 'Onboarding: Results Reveal',
    description: 'Test the archetype results page',
    icon: <Trophy className="w-4 h-4" />,
    path: '/onboarding/results',
    queryParam: 'preview=1'
  },
  {
    id: 'onboarding-full',
    name: 'Full Onboarding Flow',
    description: 'Run through the complete onboarding experience',
    icon: <Play className="w-4 h-4" />,
    path: '/onboarding',
    queryParam: 'preview=1'
  },
  {
    id: 'daily-goal',
    name: 'Daily Goal Widget',
    description: 'Test the daily goal progress UI',
    icon: <Target className="w-4 h-4" />,
    path: '/',
    queryParam: 'testGoal=1'
  },
];

export function AdminTestingPanel() {
  const navigate = useNavigate();
  const [showMobileHint, setShowMobileHint] = useState(false);

  const handleTestFlow = (flow: TestFlow) => {
    const url = flow.queryParam ? `${flow.path}?${flow.queryParam}` : flow.path;
    // Store flag so the flow knows it's a preview
    sessionStorage.setItem('adminPreview', '1');
    navigate(url);
  };

  const openInNewTab = (flow: TestFlow) => {
    const url = flow.queryParam ? `${flow.path}?${flow.queryParam}` : flow.path;
    window.open(url, '_blank');
  };

  return (
    <>
      <Card className="border-primary/30 bg-primary/5">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Play className="w-4 h-4 text-primary" />
                Admin Testing
              </CardTitle>
              <CardDescription className="text-xs mt-1">
                Preview flows without affecting your account
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs gap-1"
              onClick={() => setShowMobileHint(true)}
            >
              <Smartphone className="w-3.5 h-3.5" />
              Mobile tips
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {TEST_FLOWS.map((flow) => (
            <div
              key={flow.id}
              className="flex items-center justify-between p-2 rounded-lg bg-background/50 hover:bg-background transition-colors"
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className="text-primary shrink-0">
                  {flow.icon}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{flow.name}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{flow.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0 ml-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => openInNewTab(flow)}
                  title="Open in new tab"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  className="h-8 px-3 text-xs"
                  onClick={() => handleTestFlow(flow)}
                >
                  <Play className="w-3 h-3 mr-1" />
                  Run
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={showMobileHint} onOpenChange={setShowMobileHint}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5" />
              Testing on Mobile
            </DialogTitle>
            <DialogDescription>
              Tips for testing flows on your phone
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
              <Monitor className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Use browser DevTools</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Toggle device mode (Ctrl+Shift+M) to simulate mobile screens
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
              <Smartphone className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Test on your phone</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Open any test URL on your mobile browser while logged in to preview the actual experience
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/10">
              <Sparkles className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Preview mode enabled</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  When you run tests, changes won't affect your real profile data
                </p>
              </div>
            </div>
          </div>
          <Button 
            onClick={() => setShowMobileHint(false)}
            className="w-full mt-2"
          >
            Got it
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
